import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A custom element that keeps a WhatsApp deep link in sync with the selected variant.
 *
 * The `href` is already fully rendered server-side, so the button works without JS. This component
 * only re-resolves the variant-dependent tokens ({{variante}}, {{sku}}, {{precio}}, {{url}}) that
 * the Liquid block deliberately left untouched in `data-template`, because the variant picker only
 * morphs itself and never re-renders sibling blocks.
 *
 * @typedef {Object} Refs
 * @property {HTMLAnchorElement} link - The anchor pointing at wa.me
 *
 * @extends {Component<Refs>}
 */
class WhatsappAdvisor extends Component {
  requiredRefs = ['link'];

  /** @type {Element | null} */
  #listenerTarget = null;

  connectedCallback() {
    super.connectedCallback();
    // Cached on connect: by the time disconnectedCallback runs the element is already detached,
    // so `closest()` would return null and the listener would leak across section re-renders.
    this.#listenerTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#listenerTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listenerTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#listenerTarget = null;
  }

  /**
   * Handles product select event and rebuilds the WhatsApp link.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
          // newProduct.url is a path (`/products/foo`); data-product-url is absolute.
          if (newProduct.url) this.dataset.productUrl = new URL(newProduct.url, window.location.origin).href;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (!resource) return;

        const { link } = this.refs;
        const phone = this.dataset.phone;
        if (!phone) return;

        const message = this.#buildMessage(resource);
        link.href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[whatsapp-advisor] Event promise rejected:', error);
      });
  };

  /**
   * Substitutes the variant-dependent tokens of the message template.
   * @param {Object} variant - The selected variant, as serialized by the variant picker.
   * @returns {string} The resolved message.
   */
  #buildMessage(variant) {
    const template = this.dataset.template ?? '';
    const productUrl = this.dataset.productUrl ?? '';

    // Built from data-product-url rather than window.location so we do not depend on the variant
    // picker having already pushed the new URL into the history.
    const variantUrl = variant.id ? `${productUrl}?variant=${variant.id}` : productUrl;

    let price = '';
    if (typeof variant.price === 'number') {
      price = formatMoney(variant.price, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || '');
    }

    // Mirrors the Liquid side: never leak Shopify's placeholder variant name into the message.
    const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';

    return template
      .replaceAll('[variante]', variantTitle)
      .replaceAll('[sku]', variant.sku ?? '')
      .replaceAll('[precio]', price)
      .replaceAll('[url]', variantUrl);
  }
}

if (!customElements.get('whatsapp-advisor')) {
  customElements.define('whatsapp-advisor', WhatsappAdvisor);
}
